<script setup lang="ts">
import { computed } from 'vue';
import {
  AGENT_NAME_FORCED_IDEATION,
  AGENT_NAME_MARKET_SIZING,
  AGENT_NAME_PATENT_FTO,
  AGENT_NAME_TECH_EXPLORATION,
  AGENT_TOP_ROUTE_NAME_FORCED_IDEATION,
  AGENT_TOP_ROUTE_NAME_MARKET_SIZING,
  AGENT_TOP_ROUTE_NAME_PATENT_FTO,
  AGENT_TOP_ROUTE_NAME_TECH_EXPLORATION,
} from '@/constants';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import { FunctionKey } from '@/types';
import { useFunctionAccess } from '@/utils/composables/useFunctionAccess';
import { featureFlags } from '@/featureFlags';

const AGENTS: {
  name: string;
  description: string;
  topRouteName: string;
  isBeta: boolean;
  functionKey: FunctionKey;
}[] = [
  {
    name: AGENT_NAME_FORCED_IDEATION,
    description: '視点を変えて新しい発想を得る',
    topRouteName: AGENT_TOP_ROUTE_NAME_FORCED_IDEATION,
    isBeta: true,
    functionKey: 'forced_ideation',
  },
  {
    name: AGENT_NAME_MARKET_SIZING,
    description: '対象市場の規模を試算',
    topRouteName: AGENT_TOP_ROUTE_NAME_MARKET_SIZING,
    isBeta: true,
    functionKey: 'market_sizing',
  },
  {
    name: AGENT_NAME_TECH_EXPLORATION,
    description: '技術課題の解決策を探索',
    topRouteName: AGENT_TOP_ROUTE_NAME_TECH_EXPLORATION,
    isBeta: false,
    functionKey: 'tech_exploration',
  },
  {
    name: AGENT_NAME_PATENT_FTO,
    description: '研究に関連する特許を調査',
    topRouteName: AGENT_TOP_ROUTE_NAME_PATENT_FTO,
    isBeta: false,
    functionKey: 'patent_fto',
  },
];

const functionAccess = featureFlags.ANDEV_10131_CAPABILITY
  ? useFunctionAccess()
  : undefined;

// 取得中は利用できるか分からないので、全て出したうえで選べない状態にする
const isPending = computed(() => !!functionAccess?.isLoading.value);

const visibleAgents = computed(() =>
  AGENTS.filter(
    agent =>
      !functionAccess ||
      isPending.value ||
      functionAccess.canUse(agent.functionKey),
  ),
);
</script>

<template>
  <div v-if="visibleAgents.length > 0" class="other-agents">
    <div class="header">
      <DgrIcon name="aconnect-logo" class="aconnect-home-icon" />
      <div class="c-title-bold--xm">その他のAIエージェント</div>
    </div>
    <div class="c-text-regular--s description">
      発想・市場分析・技術探索・特許調査など、目的に応じたAIエージェントをご利用いただけます。
    </div>
    <component
      :is="isPending ? 'div' : 'router-link'"
      v-for="agent in visibleAgents"
      :key="agent.name"
      class="agent-card"
      :class="{ disabled: isPending }"
      v-bind="
        isPending
          ? { 'aria-disabled': 'true' }
          : { to: { name: agent.topRouteName }, target: '_blank' }
      "
    >
      <div class="info">
        <div class="title">
          <div class="c-title-medium--m">{{ agent.name }}</div>
          <span v-if="agent.isBeta" class="c-title-bold--xs beta">β</span>
        </div>
        <div class="c-text-regular--s description">{{ agent.description }}</div>
      </div>
      <DgrIcon
        name="chevron-right"
        size="small"
        :keep-fill="false"
        class="icon"
      />
    </component>
  </div>
</template>

<style scoped lang="scss">
.other-agents {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  padding: 12px 16px 16px 16px;
  gap: 16px;
  border: 1px solid $color-gray400;
  border-radius: 4px;
  background: #ffffff;

  .header {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .description,
  .icon {
    color: $color-gray800;
  }

  .agent-card {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background: $color-gray200;
    border-radius: 4px;

    &:hover:not(.disabled) {
      background: $color-gray400;
    }

    &.disabled {
      color: $color-gray600;
      cursor: default;
    }

    .info {
      display: flex;
      flex-direction: column;

      .title {
        display: flex;
        gap: 4px;
        align-items: center;
      }

      .beta {
        color: $color-green600;
      }
    }
  }
}
</style>
